/* ==========================================================================
   Young Grandpa - UGC detail modal
   --------------------------------------------------------------------------
   Opens snippets/yg-ugc-modal.liquid - ONE shared, structurally fixed
   template per carousel section - and repopulates it from whichever
   blocks/yg-ugc-item.liquid card is being viewed. Every field the modal
   shows already rides on the card as a data-ugc-* attribute, so opening an
   entry is a DOM read plus a handful of textContent writes: no fetch, no
   section rendering, no per-block modal markup. Prev/next walk the same
   entry list and re-run the same populate step, which is why the layout
   can never drift between "opened directly" and "arrived at by arrow".

   TAP DETECTION, and why this doesn't use a click listener
   --------------------------------------------------------
   The photo is now the trigger, and the photo is also the natural place to
   grab the carousel to drag it. Two things rule out the obvious approach:

   1. It can't be an <a> or <button>. assets/yg-ugc-carousel.js's
      onPointerDown bails on `event.target.closest('a, button')` (so that
      real controls stay clickable through its pointer capture), which
      would make the photo undraggable - the single worst place in the
      carousel to lose drag.
   2. It can't listen for `click`. That same onPointerDown calls
      viewport.setPointerCapture(), which retargets everything after it -
      including the browser's synthesized click - to the viewport element.
      A genuine tap on the photo therefore produces a click whose target is
      the viewport, not the photo, so `closest('[data-ugc-open]')` on a
      click handler finds nothing and the modal never opens. (That exact
      retargeting is what broke the old "Read more" link; see the long
      comment in assets/yg-ugc-carousel.js's onPointerDown.)

   So the gesture is resolved from pointer events directly, following the
   same movement-threshold shape as assets/yg-product-card-drag-fix.js:
   remember the pointerdown's real target and position, watch pointermove
   for movement past THRESHOLD, and on pointerup open the modal only if the
   pointer never moved past it (and the press was short enough to read as a
   tap rather than a long-press). pointerdown's own target is trustworthy -
   capture is engaged during that handler and only affects later events -
   so the saved target is the real photo even though the later pointerup
   is retargeted to the viewport. A drag of any length, in any direction,
   simply never reaches the open call.

   Keyboard has its own path (Enter/Space on the role="button" photo), which
   pointer events would never produce.
   ========================================================================== */

(function () {
  'use strict';

  var CARD = '.ugc-carousel-card';
  var TRIGGER = '[data-ugc-open]';
  var ROOT = '[data-ugc-carousel]';
  var REAL_CARD = '.ugc-carousel-card:not([data-ugc-clone])';

  /* Same 10px as assets/yg-product-card-drag-fix.js and the carousel's own
     INTENT_THRESHOLD - one consistent idea of "this was a drag, not a tap"
     across the site. */
  var THRESHOLD = 10;
  /* A press held longer than this is a long-press (image save sheet, text
     selection, or just a hesitant grab), not a tap. */
  var TAP_MAX_MS = 700;
  /* Must match the transition duration on .yg-ugc-modal in
     assets/yg-custom.css - close() waits it out before dialog.close() so
     the fade-out is actually seen. */
  var CLOSE_ANIMATION_MS = 200;
  /* How long after opening a backdrop click is ignored - see openedAt. */
  var OPEN_CLICK_GRACE_MS = 300;

  /** pointerId -> { x, y, t, trigger } */
  var gestures = new Map();

  /** The modal currently on screen, or null. */
  var active = null;

  /** A dialog.close() queued at the end of a fade-out: { modal, timer }. */
  var pendingClose = null;

  /**
   * @param {Element} modal
   * @param {string} name
   * @returns {HTMLElement|null}
   */
  function slot(modal, name) {
    return modal.querySelector('[data-ugc-modal-' + name + ']');
  }

  /**
   * The real (non-clone) cards of one carousel, in author order. Read fresh
   * every open rather than cached: the theme editor re-renders the section
   * on every edit, so a cached list would go stale the moment the founder
   * adds, removes or reorders an entry.
   *
   * @param {Element} root
   * @returns {HTMLElement[]}
   */
  function entriesOf(root) {
    return Array.prototype.slice.call(root.querySelectorAll(REAL_CARD));
  }

  /**
   * @param {number} n
   * @returns {string}
   */
  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /**
   * Sets an element's text, and hides it entirely when there's nothing to
   * show - an empty <p> would still occupy its margins and leave a gap.
   *
   * @param {HTMLElement|null} el
   * @param {string} value
   */
  function fill(el, value) {
    if (!el) return;
    el.textContent = value || '';
    el.hidden = !value;
  }

  /**
   * Writes one entry into the shared template.
   *
   * @param {Element} modal
   * @param {HTMLElement} card
   * @param {number} index
   * @param {number} total
   */
  function populate(modal, card, index, total) {
    var data = card.dataset;

    /* Two <img>s, one src: the visible photo and the full-bleed wash the
       info panel's backdrop-filter frosts (see snippets/yg-ugc-modal.liquid).
       Same URL, so the second costs no extra request. */
    var image = /** @type {HTMLImageElement|null} */ (slot(modal, 'image'));
    var wash = /** @type {HTMLImageElement|null} */ (slot(modal, 'wash'));

    /* An entry with no photo has to actively clear both <img>s, not just
       skip them. Two of the founder's live entries point at files that are
       no longer in the store (the card falls back to a placeholder), and
       leaving the previous entry's src in place made prev/next look like
       it had changed the caption but not the photo. */
    if (image) {
      if (data.ugcPhoto) {
        image.hidden = false;
        image.src = data.ugcPhoto;
        image.alt = data.ugcPhotoAlt || '';
      } else {
        image.hidden = true;
        image.removeAttribute('src');
        image.alt = '';
      }
    }

    if (wash) {
      if (data.ugcPhoto) {
        wash.hidden = false;
        wash.src = data.ugcPhoto;
      } else {
        /* No photo: the panel's own flat surface shows through the glass,
           which stays perfectly readable - it's the same tint with nothing
           behind it. */
        wash.hidden = true;
        wash.removeAttribute('src');
      }
    }

    var handle = slot(modal, 'handle');
    if (handle) {
      /* The section-level account name is the default. Only an entry that
         explicitly overrides it swaps in its own, and the default has to
         survive navigating from an overriding entry back to a plain one -
         hence caching it on first use rather than reading the live text. */
      if (handle.dataset.ugcDefault === undefined) {
        handle.dataset.ugcDefault = handle.textContent.trim();
      }
      handle.textContent = data.ugcHandle || handle.dataset.ugcDefault;
    }

    fill(slot(modal, 'likes'), data.ugcLikes);
    fill(slot(modal, 'comments'), data.ugcComments);
    fill(slot(modal, 'quote'), data.ugcQuote);
    fill(slot(modal, 'time'), data.ugcTime);

    var name = slot(modal, 'name');
    /* — rather than a literal em dash: CLAUDE.md flags em dashes as a
       corruption risk for anything in this repo that gets edited from
       PowerShell. */
    if (name) fill(name, data.ugcName ? '— ' + data.ugcName : '');

    var counter = slot(modal, 'counter');
    if (counter) counter.textContent = pad(index + 1) + ' / ' + pad(total);

    populateProduct(modal, data);
  }

  /**
   * "Shop the look" is optional per entry. With no linked product the whole
   * section is hidden rather than rendered empty, and its <a> is emptied so
   * a stale product can never be left one tap away behind a hidden panel.
   *
   * @param {Element} modal
   * @param {DOMStringMap} data
   */
  function populateProduct(modal, data) {
    var shop = slot(modal, 'shop');
    if (!shop) return;

    var link = /** @type {HTMLAnchorElement|null} */ (slot(modal, 'product-link'));
    var thumb = /** @type {HTMLImageElement|null} */ (slot(modal, 'product-image'));

    if (!data.ugcProductUrl) {
      shop.hidden = true;
      if (link) link.removeAttribute('href');
      return;
    }

    shop.hidden = false;
    if (link) link.href = data.ugcProductUrl;
    fill(slot(modal, 'product-title'), data.ugcProductTitle);
    fill(slot(modal, 'product-price'), data.ugcProductPrice);

    if (thumb) {
      thumb.hidden = !data.ugcProductImage;
      if (data.ugcProductImage) {
        thumb.src = data.ugcProductImage;
        thumb.alt = data.ugcProductTitle || '';
      }
    }
  }

  /**
   * showModal() doesn't stop the page behind from scrolling. Padding the
   * body by the width the scrollbar leaves behind keeps the header/footer
   * from jumping sideways as it disappears.
   */
  function lockScroll() {
    var gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.dataset.ugcScrollLock = 'true';
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = gap + 'px';
  }

  function unlockScroll() {
    if (!document.body.dataset.ugcScrollLock) return;
    delete document.body.dataset.ugcScrollLock;
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  /**
   * @param {Element} root
   * @param {number} index
   * @param {Element=} returnFocusTo
   */
  function open(root, index, returnFocusTo) {
    var modal = /** @type {HTMLDialogElement|null} */ (root.querySelector('[data-ugc-modal]'));
    if (!modal) return;

    var entries = entriesOf(root);
    if (!entries.length) return;

    var i = ((index % entries.length) + entries.length) % entries.length;
    populate(modal, entries[i], i, entries.length);

    if (active && active.modal === modal) {
      active.index = i;
      return;
    }

    if (active) closeNow();

    /* A close() started less than CLOSE_ANIMATION_MS ago still has a timer
       queued to call dialog.close(). Reopening inside that window - close
       the modal, immediately tap another photo - would let that timer fire
       against the freshly reopened dialog and shut it again. Cancel it, and
       don't call showModal() on a dialog that timer hasn't closed yet:
       showModal() on an already-open dialog throws InvalidStateError. */
    cancelPendingClose();

    active = {
      root: root,
      modal: modal,
      index: i,
      trigger: returnFocusTo || null,
      timer: null,
      /* Guards the backdrop-click close below. The modal opens on
         pointerup, one step ahead of the click the browser then
         synthesizes from that same tap - if that click were ever to land
         on the freshly-shown dialog, it would close the modal in the same
         gesture that opened it. */
      openedAt: performance.now(),
    };

    if (!modal.open) modal.showModal();
    lockScroll();
    /* Next frame, not this one: the transition needs a painted "closed"
       state to animate away from, and showModal() only reveals the dialog
       at the end of this tick. */
    requestAnimationFrame(function () {
      modal.classList.add('is-open');
    });
  }

  /**
   * @param {number} step
   */
  function move(step) {
    if (!active) return;
    var entries = entriesOf(active.root);
    if (!entries.length) return;
    var next = ((active.index + step) % entries.length + entries.length) % entries.length;
    active.index = next;
    populate(active.modal, entries[next], next, entries.length);
  }

  /**
   * Drops a queued "finish closing" timer without letting it fire. Used
   * whenever something reopens (or hard-closes) a modal inside the fade-out
   * window that timer belongs to.
   */
  function cancelPendingClose() {
    if (!pendingClose) return;
    clearTimeout(pendingClose.timer);
    pendingClose = null;
  }

  /** Tears the modal down immediately, skipping the fade. */
  function closeNow() {
    if (!active) return;
    var current = active;
    active = null;

    cancelPendingClose();
    clearTimeout(current.timer);
    current.modal.classList.remove('is-open');
    if (current.modal.open) current.modal.close();
    unlockScroll();

    if (current.trigger && document.contains(current.trigger)) {
      current.trigger.focus({ preventScroll: true });
    }
  }

  function close() {
    if (!active) return;
    var current = active;
    active = null;

    current.modal.classList.remove('is-open');
    cancelPendingClose();
    pendingClose = {
      modal: current.modal,
      timer: setTimeout(function () {
        pendingClose = null;
        if (current.modal.open) current.modal.close();
      }, CLOSE_ANIMATION_MS),
    };
    current.timer = pendingClose.timer;

    unlockScroll();

    if (current.trigger && document.contains(current.trigger)) {
      current.trigger.focus({ preventScroll: true });
    }
  }

  /**
   * @param {Element} trigger
   */
  function openFromTrigger(trigger) {
    var card = trigger.closest(CARD);
    var root = trigger.closest(ROOT);
    if (!card || !root) return;

    var index = Number(card.dataset.ugcIndex);
    if (!isFinite(index)) {
      /* assets/yg-ugc-carousel.js stamps data-ugc-index while building the
         loop's second half. If it hasn't run (or couldn't), fall back to
         the card's own position among the real ones. */
      index = entriesOf(root).indexOf(/** @type {HTMLElement} */ (card));
    }
    if (index < 0) return;

    open(root, index, trigger);
  }

  /* --------------------------------------------------- gesture handling */

  /**
   * @param {PointerEvent} event
   */
  function onPointerDown(event) {
    var trigger = event.target instanceof Element ? event.target.closest(TRIGGER) : null;
    if (!trigger) return;

    gestures.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      t: performance.now(),
      trigger: trigger,
    });
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerMove(event) {
    var gesture = gestures.get(event.pointerId);
    if (!gesture) return;

    if (
      Math.abs(event.clientX - gesture.x) > THRESHOLD ||
      Math.abs(event.clientY - gesture.y) > THRESHOLD
    ) {
      /* Past the threshold this is a drag, and it stays a drag even if the
         finger comes back to where it started - dropping the gesture is
         what guarantees the release can't open anything. */
      gestures.delete(event.pointerId);
    }
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerUp(event) {
    var gesture = gestures.get(event.pointerId);
    if (!gesture) return;
    gestures.delete(event.pointerId);

    if (performance.now() - gesture.t > TAP_MAX_MS) return;
    openFromTrigger(gesture.trigger);
  }

  /**
   * @param {PointerEvent} event
   */
  function onPointerCancel(event) {
    gestures.delete(event.pointerId);
  }

  /**
   * @param {KeyboardEvent} event
   */
  function onKeydown(event) {
    if (active) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      }
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var trigger = event.target instanceof Element ? event.target.closest(TRIGGER) : null;
    if (!trigger || trigger.getAttribute('tabindex') === '-1') return;

    event.preventDefault();
    openFromTrigger(trigger);
  }

  /**
   * @param {MouseEvent} event
   */
  function onClick(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-ugc-modal-close]')) {
      close();
      return;
    }
    if (event.target.closest('[data-ugc-modal-prev]')) {
      move(-1);
      return;
    }
    if (event.target.closest('[data-ugc-modal-next]')) {
      move(1);
      return;
    }

    /* The dialog element itself is the full-viewport surface around the
       panel, so a click landing on it (rather than on anything inside
       .yg-ugc-modal__panel) is a click on the dimmed page. */
    if (!active || performance.now() - active.openedAt < OPEN_CLICK_GRACE_MS) return;
    if (event.target.matches('[data-ugc-modal]')) close();
  }

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointermove', onPointerMove, true);
  document.addEventListener('pointerup', onPointerUp, true);
  document.addEventListener('pointercancel', onPointerCancel, true);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('click', onClick);

  /* Escape fires the dialog's own `cancel`, which would close it instantly
     and skip the fade. Cancel that and route through close() instead. */
  document.addEventListener(
    'cancel',
    function (event) {
      if (!active || event.target !== active.modal) return;
      event.preventDefault();
      close();
    },
    true
  );

  /* A section re-render in the theme editor replaces the dialog node out
     from under us; drop the reference rather than leaving the scroll lock
     applied to a modal that no longer exists. */
  document.addEventListener('shopify:section:unload', function () {
    if (active && !document.contains(active.modal)) {
      active = null;
      unlockScroll();
    }
  });
})();
